import React, { useEffect, useRef } from 'react';

const MART_FEATURES = [
  { icon: '📦', text: 'Full Product & Inventory Management' },
  { icon: '👥', text: 'Customer & Vendor Management' },
  { icon: '📊', text: 'Advanced Sales Analytics & Reporting' },
  { icon: '🏪', text: 'Multi-Store & Branch Management' },
  { icon: '🎉', text: 'Offers, Promotions & Advertisement' },
  { icon: '🚚', text: 'Integrated Delivery Management' },
  { icon: '📋', text: 'Purchase Orders & Invoices' },
  { icon: '⚙️', text: 'APS & Configuration Settings' },
];

const POS_FEATURES = [
  { icon: '🧾', text: 'Instant POS Billing & Checkout' },
  { icon: '📱', text: 'Mobile & Delivery Orders' },
  { icon: '⚡', text: 'Quick Commerce Support' },
  { icon: '📦', text: 'Bulk Order & Party Order' },
  { icon: '📜', text: 'Full Order History' },
  { icon: '💳', text: 'Cash, Online & COD Payments' },
  { icon: '🔍', text: 'Barcode Scan at Counter' },
  { icon: '⏰', text: 'Business Hour Configuration' },
];

export default function Products() {
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.1 });
    ref.current?.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const nav = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="section" id="products" style={{ background: '#f9fafb' }} ref={ref}>
      <div className="container">
        <div className="reveal" style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 64px' }}>
          <span className="eyebrow">Two Powerful Products</span>
          <h2 className="t-section" style={{ color: '#0c1220', marginBottom: 16 }}>One Platform, Built for Retail</h2>
          <p style={{ fontSize: 17, color: '#6b7280', lineHeight: 1.7 }}>
            Mart CMS handles your back-office management. POS-Lite powers your billing counters. Together, they cover every corner of your retail operation.
          </p>
        </div>

        <div className="g2">
          {/* Mart CMS Card */}
          <div className="reveal d1" style={{
            background: '#111827', borderRadius: 20, padding: '36px',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg,#1e3a8a,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🖥</div>
              <div>
                <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: 22, color: 'white', letterSpacing: '-0.5px' }}>Mart CMS</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>Management Portal</div>
              </div>
            </div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: 24, marginTop: 12 }}>
              The complete back-office command centre for your retail store. Manage everything from products to vendors, inventory to deliveries — all from one powerful dashboard.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
              {MART_FEATURES.map(f => (
                <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{f.icon}</span>
                  <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{f.text}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 28, display: 'flex', gap: 10 }}>
              <button className="btn btn-blue" style={{ flex: 1, justifyContent: 'center' }} onClick={() => nav('modules')}>Explore Features</button>
              <button className="btn btn-ghost btn-sm" onClick={() => nav('contact')}>Demo →</button>
            </div>
          </div>

          {/* POS-Lite Card */}
          <div className="reveal d2" style={{
            background: 'linear-gradient(160deg,#1e3a8a 0%,#1d4ed8 50%,#2563eb 100%)',
            borderRadius: 20, padding: '36px',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 20px 60px rgba(37,99,235,0.25)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, backdropFilter: 'blur(4px)' }}>🧾</div>
              <div>
                <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: 22, color: 'white', letterSpacing: '-0.5px' }}>POS-Lite</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>Billing & Counter Management</div>
              </div>
            </div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, marginBottom: 24, marginTop: 12 }}>
              A fast, intuitive point-of-sale system built for speed at the counter. Handle billing, quick commerce, bulk orders, and party orders without slowing down your staff.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
              {POS_FEATURES.map(f => (
                <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{f.icon}</span>
                  <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>{f.text}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 28, display: 'flex', gap: 10 }}>
              <button className="btn btn-white" style={{ flex: 1, justifyContent: 'center' }} onClick={() => nav('modules')}>Explore Features</button>
              <button style={{
                padding: '10px 18px', border: '1.5px solid rgba(255,255,255,0.35)', borderRadius: 10,
                background: 'transparent', color: 'white', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif',
              }} onClick={() => nav('contact')}>Demo →</button>
            </div>
          </div>
        </div>

        {/* Apps row */}
        <div className="reveal" style={{ marginTop: 28, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {[
            { icon: '📱', title: 'Customer App', desc: 'Let your customers browse, order, track deliveries, and manage their accounts from a dedicated mobile app.', color: '#059669', bg: 'rgba(5,150,105,0.08)', border: 'rgba(5,150,105,0.2)' },
            { icon: '🏍', title: 'Porter App', desc: 'Equip your delivery partners with real-time order assignment, navigation, and proof-of-delivery capture.', color: '#d97706', bg: 'rgba(217,119,6,0.08)', border: 'rgba(217,119,6,0.2)' },
          ].map(app => (
            <div key={app.title} style={{ background: app.bg, border: `1.5px solid ${app.border}`, borderRadius: 16, padding: '24px 28px', display: 'flex', alignItems: 'flex-start', gap: 18 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: app.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0, border: `1px solid ${app.color}30` }}>{app.icon}</div>
              <div>
                <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: 18, color: '#111827', marginBottom: 6, letterSpacing: '-0.3px' }}>{app.title}</div>
                <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>{app.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
